import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/glossyApi";
import { Eye, EyeOff } from "lucide-react";

export default function Auth({ mode = "login" }) {
  const isLogin = mode === "login";
  const { login, register, logout, user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/portal";
  const destFor = (u) => (u.role === "admin" ? "/admin" : next.startsWith("/admin") ? "/portal" : next);
  useEffect(() => {
    if (user) navigate(destFor(user), { replace: true });
  }, [user, navigate, next]); // eslint-disable-line react-hooks/exhaustive-deps
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = isLogin
        ? await login(form.email, form.password)
        : await register(form.name, form.email, form.password);
      if (u.role === "admin") {
        logout();
        setError("This is the members' entrance. Editors have their own private door — sign in at /studio.");
        setLoading(false);
        return;
      }
      navigate(destFor(u));
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-cream">
      {/* Visual */}
      <div className="hidden lg:block relative bg-ink grain overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1667419136229-ce2c6e127a43?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt="S/ME"
          className="absolute inset-0 w-full h-full object-cover opacity-80 animate-kenburns"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-black/40" />
        <div className="absolute bottom-0 p-14 z-10">
          <Link to="/" className="font-serif text-cream text-4xl">S/ME</Link>
          <p className="font-serif italic text-cream/80 text-2xl mt-4 max-w-sm">Business, made beautiful. For the UK's stylish SMEs.</p>
        </div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center px-5 sm:px-12 py-16">
        <div className="w-full max-w-md">
          <Link to="/" className="font-serif text-3xl lg:hidden block mb-10">S/ME</Link>
          <Link to="/" className="overline text-ink/50 hover:text-ink transition-colors inline-flex items-center gap-2 mb-8" data-testid="back-to-magazine-link">← Back to the magazine</Link>
          <p className="overline text-gloss-silver mb-4">{isLogin ? "Welcome back" : "Join the magazine"}</p>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mb-3">{isLogin ? "Sign in" : "Create account"}</h1>
          <p className="font-sans text-sm text-ink/55 mb-8">{isLogin ? "Straight back to your member desk." : "Takes less than a minute — then straight to your desk."}</p>

          {error && <div className="bg-red-500/10 border border-red-500 text-red-600 px-4 py-3 font-sans text-sm mb-6" data-testid="auth-error">{error}</div>}

          <form onSubmit={submit} className="space-y-6" data-testid="auth-form">
            {!isLogin && (
              <Field label="Full name" testid="name-input" type="text" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required autoFocus />
            )}
            <Field label="Email" testid="email-input" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required autoComplete={isLogin ? "email" : "off"} autoFocus={isLogin} />
            <Field label="Password" testid="password-input" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} required autoComplete={isLogin ? "current-password" : "new-password"} />
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-ink text-cream py-4 overline hover:bg-ink transition-colors disabled:opacity-60"
              data-testid="auth-submit"
            >
              {loading ? "Please wait…" : isLogin ? "Sign in" : "Create account"}
            </button>
          </form>

          <p className="font-sans text-sm text-ink/60 mt-8">
            {isLogin ? "Not a member yet? " : "Already have an account? "}
            <Link to={isLogin ? "/register" : "/login"} className="text-ink link-underline font-medium" data-testid="auth-switch">
              {isLogin ? "Create one" : "Sign in"}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

function Field({ label, type, value, onChange, required, testid, autoComplete, autoFocus }) {
  const [show, setShow] = useState(false);
  const isPassword = type === "password";
  return (
    <label className="block">
      <span className="overline text-ink/50">{label}</span>
      <div className="relative">
        <input
          type={isPassword && show ? "text" : type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          data-testid={testid}
          className={`w-full bg-transparent border-b border-ink/30 py-3 mt-2 font-sans text-lg focus:outline-none focus:border-gloss-silver transition-colors ${isPassword ? "pr-10" : ""}`}
        />
        {isPassword && (
          <button type="button" onClick={() => setShow(!show)} tabIndex={-1} aria-label={show ? "Hide password" : "Show password"}
            className="absolute right-0 bottom-3 text-ink/40 hover:text-ink transition-colors" data-testid={`${testid}-toggle`}>
            {show ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        )}
      </div>
    </label>
  );
}
